import { IAsset, ICategory } from '../../types/damTypes';

// Keep logic aligned with desktop AssetGrid/AssetDetails.
// Root category id (prod: "Beställ material", staging: "Butiksmaterial")
const STORE_MATERIAL_ROOT_CATEGORY_IDS = ['682c6c2078cc40e17f2354cf'];

const STORE_CATEGORY_NAMES = new Set([
  'Butiksmaterial',
  'Skyltmaterial',
  'Prisskiltar',
  'Kampanjmaterial',
  'Broschyrer',
  'Menyer',
]);

type CategoryLookup = {
  byId: Map<string, ICategory>;
  parentById: Map<string, string | null>;
  validRootIds: Set<string>;
};

const lookupCache = new WeakMap<ICategory[], CategoryLookup>();

function getParentId(cat: ICategory): string | null {
  const p: any = (cat as any).parentCategory;
  if (!p) return null;
  if (typeof p === 'string') return p;
  if (typeof p === 'object' && typeof p._id === 'string') return p._id;
  return null;
}

function getLookup(allCategories: ICategory[]): CategoryLookup {
  const cached = lookupCache.get(allCategories);
  if (cached) return cached;

  const byId = new Map<string, ICategory>();
  const parentById = new Map<string, string | null>();
  for (const c of allCategories || []) {
    if (!c || !c._id) continue;
    byId.set(c._id, c);
    parentById.set(c._id, getParentId(c));
  }

  const validRootIds = new Set<string>();
  for (const id of STORE_MATERIAL_ROOT_CATEGORY_IDS) {
    if (byId.has(id)) validRootIds.add(id);
  }

  const lookup = { byId, parentById, validRootIds };
  lookupCache.set(allCategories, lookup);
  return lookup;
}

export function isStoreAsset(asset: IAsset, allCategories: ICategory[]): boolean {
  if (!asset.categories || asset.categories.length === 0) return false;

  const { byId, parentById, validRootIds } = getLookup(allCategories);

  // Preferred: ID-based (any descendant under root)
  if (validRootIds.size > 0) {
    for (const categoryId of asset.categories) {
      let current: string | null | undefined = categoryId;
      const visited = new Set<string>();
      while (current) {
        if (validRootIds.has(current)) return true;
        if (visited.has(current)) break;
        visited.add(current);
        current = parentById.get(current) || null;
      }
    }
  }

  // Safety fallback: legacy direct-name match
  for (const categoryId of asset.categories) {
    const category = byId.get(categoryId);
    if (category && STORE_CATEGORY_NAMES.has(category.name)) return true;
  }
  return false;
}

